import PropTypes from 'prop-types';
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import RadarIcon from '@mui/icons-material/Radar';
import InsightsIcon from '@mui/icons-material/Insights';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatNumber,
  formatPercent,
  formatRatio,
  getSetupMeta,
  getValidationMeta,
  marketQualityBg,
  marketQualityColor,
  normalizeHeadline,
  prettyDate,
  toneBg,
  toneColor,
  translateSemantic,
} from './shared';

/* ────────────────────────────────────────────────────────────────────────── */
/* Shared styles                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

const surfaceSx = {
  bgcolor: '#fff',
  borderRadius: 2,
  border: '1px solid #e6ebf2',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  overflow: 'hidden',
};

const tableHeadSx = {
  fontSize: '0.7rem',
  fontWeight: 800,
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  py: 0.85,
  whiteSpace: 'nowrap',
};

const tableCellSx = {
  fontSize: '0.74rem',
  color: '#334155',
  py: 0.85,
  borderBottom: '1px solid #f1f5f9',
};

const toneToAlertSeverity = (tone) => {
  if (tone === 'success') return 'success';
  if (tone === 'danger') return 'error';
  if (tone === 'warning') return 'warning';
  return 'info';
};

/**
 * Pick a localized sector name from a row's backend-provided fields.
 * Mirrors the pattern used in DarvasBox / ICBIndustry pages — backend supplies
 * `sector_name_vi` / `sector_name_en`; UI just renders the right one.
 */
const sectorDisplay = (row, lang) => {
  if (!row) return '—';
  const vi = row.sector_name_vi;
  const en = row.sector_name_en;
  const code = row.sector;
  if (lang === 'vi') return vi || en || code || '—';
  return en || vi || code || '—';
};

/**
 * Render a bilingual {en, vi} payload from the backend.
 * Accepts plain strings (legacy) and returns them as-is.
 * Falls back gracefully when one language is missing.
 */
const pickLang = (value, lang) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'object') {
    if (lang === 'vi') return value.vi || value.en || '';
    return value.en || value.vi || '';
  }
  return String(value);
};

/* ────────────────────────────────────────────────────────────────────────── */
/* PanelHeader — compact section header used across panels                    */
/* ────────────────────────────────────────────────────────────────────────── */

function PanelHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.25,
        px: 1.5,
        py: 1,
        borderBottom: '1px solid #f1f5f9',
        bgcolor: '#fbfcfe',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {Icon ? (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: 0.75,
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 13, color: '#fff' }} />
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.15 }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              noWrap
              sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.1 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {action}
    </Box>
  );
}

PanelHeader.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  action: PropTypes.node,
};

PanelHeader.defaultProps = {
  icon: null,
  subtitle: '',
  action: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarHero — slim header strip                                              */
/* ────────────────────────────────────────────────────────────────────────── */

export function RadarHero({ summary, candidateSummary, loading, refreshing, onRefresh, t, locale }) {
  const headlineMeta = normalizeHeadline(candidateSummary || {});
  const headlineTone = toneColor[headlineMeta.tone] || '#3b82f6';
  const tradeDateLabel = prettyDate(summary?.trade_date || summary?.as_of_date, locale);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
      {/* Title block */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(59, 130, 246, 0.25)',
          }}
        >
          <RadarIcon sx={{ fontSize: 22, color: '#fff' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
              {t('marketInternalRadar.title', 'Market Internal Radar')}
            </Typography>
            {tradeDateLabel ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6', boxShadow: '0 0 6px #3b82f6' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                  {`${t('marketInternalRadar.asOf', 'As of')}: ${tradeDateLabel}`}
                </Typography>
              </Box>
            ) : null}
            {headlineMeta.labelKey ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.6,
                  bgcolor: `${headlineTone}14`,
                  px: 1,
                  py: 0.3,
                  borderRadius: 1.5,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: headlineTone }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: headlineTone }}>
                  {t(headlineMeta.labelKey, headlineMeta.fallback)}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
            {t(
              'marketInternalRadar.subtitle',
              'Track breakout breadth, breakdown pressure, sector confirmation, and validated setups to judge market money flow quality.',
            )}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        <Button
          variant="contained"
          startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
          onClick={onRefresh}
          disabled={refreshing || loading}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
            borderRadius: '4px',
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            '& .MuiButton-startIcon': { mr: 0.5 },
          }}
        >
          {t('marketInternalRadar.refresh', 'Refresh Radar')}
        </Button>
      </Box>
    </Box>
  );
}

RadarHero.propTypes = {
  summary: PropTypes.shape({
    trade_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    as_of_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }),
  candidateSummary: PropTypes.shape({
    headline: PropTypes.shape({ key: PropTypes.string, label_key: PropTypes.string, tone: PropTypes.string }),
  }),
  loading: PropTypes.bool.isRequired,
  refreshing: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
};

RadarHero.defaultProps = {
  summary: null,
  candidateSummary: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarFilters — compact toolbar with chip toggles + segmented controls      */
/* ────────────────────────────────────────────────────────────────────────── */

const RANK_OPTIONS = ['LEADER', 'WATCHLIST', 'WAIT_CONFIRM', 'RISK_AVOID', 'IGNORE'];

const DEFAULT_FILTERS = {
  tradeDate: '',
  minScore: 50,
  rankLabel: '',
  sector: '',
  historyFreq: 'D',
  validatedOnly: false,
};

function FilterFieldLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: '0.62rem',
        fontWeight: 700,
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        mb: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

FilterFieldLabel.propTypes = { children: PropTypes.node.isRequired };

export function RadarFilters({ filters, onChange, onApply, loading, t }) {
  const minScoreValue = Number(filters.minScore) || 0;
  const activeCount = [
    filters.tradeDate,
    minScoreValue > 0 && minScoreValue !== DEFAULT_FILTERS.minScore,
    filters.rankLabel,
    filters.sector,
    filters.validatedOnly,
    filters.historyFreq && filters.historyFreq !== DEFAULT_FILTERS.historyFreq,
  ].filter(Boolean).length;

  const handleClear = () => {
    Object.entries(DEFAULT_FILTERS).forEach(([key, value]) => onChange(key, value));
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.78rem',
      height: 32,
      bgcolor: '#fff',
      borderRadius: 1,
    },
    '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
  };

  const chipSelectedSx = {
    bgcolor: '#0f172a',
    color: '#fff',
    fontWeight: 700,
    '&:hover': { bgcolor: '#1e293b' },
  };
  const chipDefaultSx = {
    bgcolor: '#f1f5f9',
    color: '#475569',
    fontWeight: 600,
    '&:hover': { bgcolor: '#e2e8f0' },
  };

  const toggleSx = {
    minWidth: 36,
    height: 32,
    px: 1,
    py: 0,
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'none',
    color: '#475569',
    border: '1px solid #e2e8f0',
    '&.Mui-selected': {
      bgcolor: '#0f172a',
      color: '#fff',
      borderColor: '#0f172a',
      '&:hover': { bgcolor: '#1e293b' },
    },
  };

  return (
    <Box sx={{ ...surfaceSx, px: 1.5, py: 1.25 }}>
      <Stack spacing={1.25}>
        {/* Row 1 — primary inputs + actions */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          useFlexGap
          flexWrap="wrap"
        >
          {/* Filters label + active count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#0f172a', mr: 0.25, mb: { xs: 0, md: 0.25 } }}>
            <TuneIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {t('marketInternalRadar.filters.title', 'Filters')}
            </Typography>
            {activeCount > 0 ? (
              <Chip
                size="small"
                label={activeCount}
                sx={{ bgcolor: '#0f172a', color: '#fff', height: 18, minWidth: 22, fontSize: '0.66rem', fontWeight: 800, '& .MuiChip-label': { px: 0.75 } }}
              />
            ) : null}
          </Box>

          {/* Trade date */}
          <Box sx={{ minWidth: 152 }}>
            <FilterFieldLabel>{t('marketInternalRadar.filters.tradeDate', 'Trade Date')}</FilterFieldLabel>
            <TextField
              type="date"
              size="small"
              value={filters.tradeDate}
              onChange={(event) => onChange('tradeDate', event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              sx={inputSx}
            />
          </Box>

          {/* Min Score slider */}
          <Box sx={{ minWidth: { xs: '100%', md: 200 }, flex: { xs: 'none', md: '0 1 220px' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
              <FilterFieldLabel>{t('marketInternalRadar.filters.minScore', 'Min Score')}</FilterFieldLabel>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                {minScoreValue}
              </Typography>
            </Box>
            <Slider
              value={minScoreValue}
              min={0}
              max={100}
              step={5}
              onChange={(event, value) => onChange('minScore', value)}
              valueLabelDisplay="auto"
              sx={{
                py: 0.5,
                color: '#0f172a',
                '& .MuiSlider-thumb': { width: 14, height: 14 },
                '& .MuiSlider-rail': { opacity: 0.35, bgcolor: '#cbd5e1' },
              }}
            />
          </Box>

          {/* Sector */}
          <Box sx={{ minWidth: 160, flex: { xs: 'none', md: '0 1 180px' } }}>
            <FilterFieldLabel>{t('marketInternalRadar.filters.sector', 'Sector')}</FilterFieldLabel>
            <TextField
              size="small"
              value={filters.sector}
              onChange={(event) => onChange('sector', event.target.value)}
              placeholder={t('marketInternalRadar.filters.sectorPlaceholder', 'e.g. L4_BANKS')}
              fullWidth
              sx={inputSx}
            />
          </Box>

          {/* History Freq segmented */}
          <Box>
            <FilterFieldLabel>{t('marketInternalRadar.filters.historyFreq', 'History Freq')}</FilterFieldLabel>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filters.historyFreq || 'D'}
              onChange={(event, value) => { if (value) onChange('historyFreq', value); }}
              sx={{ '& .MuiToggleButton-root': toggleSx }}
            >
              <ToggleButton value="D">{t('marketInternalRadar.filters.freqDailyShort', 'D')}</ToggleButton>
              <ToggleButton value="W">{t('marketInternalRadar.filters.freqWeeklyShort', 'W')}</ToggleButton>
              <ToggleButton value="M">{t('marketInternalRadar.filters.freqMonthlyShort', 'M')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Validated only switch */}
          <Box sx={{ display: 'flex', alignItems: 'center', height: 32, alignSelf: { xs: 'flex-start', md: 'flex-end' }, mb: { xs: 0, md: 0 } }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filters.validatedOnly}
                  onChange={(event) => onChange('validatedOnly', event.target.checked)}
                />
              }
              label={t('marketInternalRadar.filters.validatedOnly', 'Validated only')}
              sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.74rem', fontWeight: 600, color: '#475569' } }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Actions */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              variant="text"
              onClick={handleClear}
              disabled={loading || activeCount === 0}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 700,
                color: '#64748b',
                height: 32,
                minWidth: 0,
                px: 1.25,
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              {t('marketInternalRadar.filters.clear', 'Clear')}
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
              onClick={onApply}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontSize: 12.5,
                fontWeight: 700,
                borderRadius: 1,
                boxShadow: 'none',
                bgcolor: '#0f172a',
                height: 32,
                px: 1.5,
                '&:hover': { bgcolor: '#1e293b', boxShadow: 'none' },
              }}
            >
              {t('marketInternalRadar.filters.apply', 'Apply')}
            </Button>
          </Stack>
        </Stack>

        {/* Row 2 — Rank Label chip toggles */}
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ borderTop: '1px dashed #e2e8f0', pt: 1 }}
        >
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', mr: 0.5 }}>
            {`${t('marketInternalRadar.filters.rankLabel', 'Rank Label')}:`}
          </Typography>
          <Chip
            size="small"
            label={t('marketInternalRadar.filters.all', 'All')}
            onClick={() => onChange('rankLabel', '')}
            sx={{ height: 24, fontSize: '0.7rem', ...(filters.rankLabel ? chipDefaultSx : chipSelectedSx) }}
          />
          {RANK_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              size="small"
              label={opt}
              onClick={() => onChange('rankLabel', opt)}
              sx={{ height: 24, fontSize: '0.7rem', ...((filters.rankLabel === opt) ? chipSelectedSx : chipDefaultSx) }}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

RadarFilters.propTypes = {
  filters: PropTypes.shape({
    tradeDate: PropTypes.string,
    minScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rankLabel: PropTypes.string,
    sector: PropTypes.string,
    historyFreq: PropTypes.string,
    validatedOnly: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarOverview — single calm hero card replacing the 4-card row             */
/* ────────────────────────────────────────────────────────────────────────── */

function MetricTile({ label, value, color, hint }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        noWrap
        sx={{
          fontSize: '0.62rem',
          fontWeight: 700,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: color || '#0f172a', lineHeight: 1 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', mt: 0.35 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

MetricTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string,
  hint: PropTypes.string,
};

MetricTile.defaultProps = {
  color: '',
  hint: '',
};

function NarrativeTone({ label, text, tone }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>
        {`${label}:`}
      </Typography>
      <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 800, color: toneColor[tone] || '#0f172a' }}>
        {text}
      </Typography>
    </Box>
  );
}

NarrativeTone.propTypes = {
  label: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  tone: PropTypes.string,
};

NarrativeTone.defaultProps = {
  tone: 'info',
};

export function RadarOverview({ summary, candidateSummary, t }) {
  const s = summary || {};
  const cs = candidateSummary || {};
  const semantics = s.semantics || {};
  const warnings = semantics.risk_warnings || [];

  const marketLabel = s.market_quality_label || 'UNKNOWN';
  const qualityScore = Number(s.market_quality_score || 0);
  const qColor = marketQualityColor(marketLabel);

  const breakouts = (s.breakout_20_count || 0) + (s.breakout_60_count || 0);
  const breakdowns = (s.breakdown_20_count || 0) + (s.breakdown_60_count || 0);
  const validated = cs.validated_count || 0;
  const fragile = cs.fragile_count || 0;

  const ma20 = (s.pct_above_ma20 || 0) * 100;
  const ma50 = (s.pct_above_ma50 || 0) * 100;
  const leaders = s.leaders_count || 0;
  const watchlist = s.watchlist_count || 0;

  const headlineText = translateSemantic(
    t,
    semantics.headline,
    t('marketInternalRadar.semantics.defaultHeadline', 'Market internals are mixed.'),
  );
  const biasText = translateSemantic(t, semantics.market_bias, semantics.market_bias?.key || '—');
  const indexText = translateSemantic(t, semantics.index_read, semantics.index_read?.key || '—');

  return (
    <Box sx={surfaceSx}>
      {/* Lean header — single icon + title, no gradient or subtitle */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <RadarIcon sx={{ fontSize: 18, color: '#475569' }} />
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
          {t('marketInternalRadar.overview.title', 'Market Overview')}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        {/* Hero — score + narrative */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr)' },
            gap: { xs: 1.5, md: 3 },
            alignItems: 'center',
            py: 1,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('marketInternalRadar.stats.marketQuality', 'Market Quality')}
              </Typography>
              <Box
                sx={{
                  px: 0.85,
                  py: 0.1,
                  borderRadius: 999,
                  bgcolor: qColor,
                  color: '#fff',
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  letterSpacing: 0.5,
                }}
              >
                {marketLabel}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontSize: '2.4rem', fontWeight: 900, color: qColor, lineHeight: 1 }}>
                {formatNumber(qualityScore, 2)}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 700 }}>
                / 100
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.94rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
              {headlineText}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.5, sm: 2.5 }}
              sx={{ mt: 0.85 }}
              useFlexGap
              flexWrap="wrap"
            >
              <NarrativeTone
                label={t('marketInternalRadar.bias', 'Bias')}
                text={biasText}
                tone={semantics.market_bias?.tone}
              />
              <NarrativeTone
                label={t('marketInternalRadar.indexRead', 'Index Read')}
                text={indexText}
                tone={semantics.index_read?.tone}
              />
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5, borderColor: '#eef2f7' }} />

        {/* Metric strip — 8 flat tiles, no borders, no bars */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(8, minmax(0, 1fr))',
            },
            gap: { xs: 1.5, lg: 2 },
            columnGap: { lg: 1 },
          }}
        >
          <MetricTile
            label={t('marketInternalRadar.overview.breakouts', 'Breakouts')}
            value={breakouts}
            color="#10b981"
            hint="20D + 60D"
          />
          <MetricTile
            label={t('marketInternalRadar.overview.breakdowns', 'Breakdowns')}
            value={breakdowns}
            color="#ef4444"
            hint="20D + 60D"
          />
          <MetricTile
            label={t('marketInternalRadar.candidates.validated', 'Validated')}
            value={validated}
            color="#2563eb"
          />
          <MetricTile
            label={t('marketInternalRadar.candidates.fragile', 'Fragile')}
            value={fragile}
            color="#d97706"
          />
          <MetricTile
            label={t('marketInternalRadar.overview.ma20Short', '% MA20')}
            value={formatPercent(ma20, 1)}
            color="#2563eb"
          />
          <MetricTile
            label={t('marketInternalRadar.overview.ma50Short', '% MA50')}
            value={formatPercent(ma50, 1)}
            color="#6366f1"
          />
          <MetricTile
            label={t('marketInternalRadar.summary.leaders', 'Leaders')}
            value={leaders}
            color="#10b981"
          />
          <MetricTile
            label={t('marketInternalRadar.summary.watchlist', 'Watchlist')}
            value={watchlist}
            color="#0ea5e9"
          />
        </Box>

        {/* Risk warnings — single inline row, only when present */}
        {warnings.length ? (
          <>
            <Divider sx={{ my: 1.5, borderColor: '#eef2f7' }} />
            <Box>
              <Typography
                sx={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mb: 0.75,
                }}
              >
                {t('marketInternalRadar.semantics.riskTitle', 'Risk Warnings')}
              </Typography>
              <Stack direction="row" spacing={2.5} useFlexGap flexWrap="wrap">
                {warnings.map((w) => {
                  const wTone = w.tone || 'warning';
                  return (
                    <Box key={w.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                      <WarningAmberIcon sx={{ fontSize: '0.95rem', color: toneColor[wTone] }} />
                      <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#475569' }}>
                        {translateSemantic(t, w, w.key)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: toneColor[wTone] }}>
                        {w.value ?? 0}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

RadarOverview.propTypes = {
  summary: PropTypes.shape({
    market_quality_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    market_quality_label: PropTypes.string,
    breakout_20_count: PropTypes.number,
    breakout_60_count: PropTypes.number,
    breakdown_20_count: PropTypes.number,
    breakdown_60_count: PropTypes.number,
    pct_above_ma20: PropTypes.number,
    pct_above_ma50: PropTypes.number,
    leaders_count: PropTypes.number,
    watchlist_count: PropTypes.number,
    semantics: PropTypes.shape({
      headline: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string, label_key: PropTypes.string }),
      market_bias: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string, label_key: PropTypes.string }),
      index_read: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string, label_key: PropTypes.string }),
      risk_warnings: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        tone: PropTypes.string,
        label_key: PropTypes.string,
        value: PropTypes.number,
      })),
    }),
  }),
  candidateSummary: PropTypes.shape({
    validated_count: PropTypes.number,
    fragile_count: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};

RadarOverview.defaultProps = {
  summary: null,
  candidateSummary: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarQualityBreakdown — investor verification: 8 pillars summing to score   */
/* ────────────────────────────────────────────────────────────────────────── */

function PillarRow({ pillar, t }) {
  const contribution = Number(pillar.contribution) || 0;
  const positive = contribution >= 0;
  const magnitude = Math.abs(contribution);
  const max = Math.max(Number(pillar.max_contribution) || 0, magnitude, 1);
  const widthPct = Math.min(100, (magnitude / max) * 100);
  const barColor = toneColor[pillar.tone] || '#94a3b8';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '210px 90px minmax(0, 1fr) minmax(0, 1.4fr)' },
        gap: { xs: 0.5, md: 1.5 },
        alignItems: 'center',
        py: 0.6,
        borderBottom: '1px dashed #eef2f7',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
        {t(pillar.label_key, pillar.key)}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.85rem',
          fontWeight: 800,
          color: barColor,
          textAlign: { xs: 'left', md: 'right' },
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {`${positive ? '+' : ''}${contribution.toFixed(2)}`}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Box sx={{ flex: 1, height: 6, borderRadius: 999, bgcolor: '#f1f5f9', overflow: 'hidden', position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0, width: `${widthPct}%`, bgcolor: barColor, borderRadius: 999, transition: 'width 0.25s ease' }} />
        </Box>
        <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 600, minWidth: 44, textAlign: 'right' }}>
          {`/ ${(Number(pillar.max_contribution) || 0).toFixed(0)}`}
        </Typography>
      </Box>
      <Typography
        title={pillar.raw || ''}
        sx={{ fontSize: '0.72rem', color: '#64748b', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {pillar.raw || ''}
      </Typography>
    </Box>
  );
}

PillarRow.propTypes = {
  pillar: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label_key: PropTypes.string,
    contribution: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max_contribution: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tone: PropTypes.string,
    raw: PropTypes.string,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

export function RadarQualityBreakdown({ summary, t }) {
  const pillars = summary?.pillars || [];
  if (!pillars.length) return null;
  const score = Number(summary?.market_quality_score || 0);
  const total = pillars.reduce((acc, p) => acc + (Number(p.contribution) || 0), 0);
  return (
    <Box sx={surfaceSx}>
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InsightsIcon sx={{ fontSize: 18, color: '#475569' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
            {t('marketInternalRadar.breakdown.title', 'Why this score? · Quality decomposition')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
            {t('marketInternalRadar.breakdown.subtitle', '8 pillars summing to the market quality score')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {t('marketInternalRadar.breakdown.total', 'Total')}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
            {total.toFixed(2)}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700 }}>
            {`≈ ${score.toFixed(2)} / 100`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2, pb: 1.5 }}>
        {pillars.map((p) => <PillarRow key={p.key} pillar={p} t={t} />)}
      </Box>
    </Box>
  );
}

RadarQualityBreakdown.propTypes = {
  summary: PropTypes.shape({
    market_quality_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pillars: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label_key: PropTypes.string,
      contribution: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max_contribution: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      tone: PropTypes.string,
      raw: PropTypes.string,
    })),
  }),
  t: PropTypes.func.isRequired,
};

RadarQualityBreakdown.defaultProps = {
  summary: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarEvidenceBoard — concrete names backing every headline metric          */
/* ────────────────────────────────────────────────────────────────────────── */

function MetricChip({ label, value, tone }) {
  const color = toneColor[tone] || '#64748b';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.4,
        px: 0.6,
        py: 0.15,
        borderRadius: 0.75,
        bgcolor: `${color}10`,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.58rem',
          fontWeight: 700,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        noWrap
        sx={{
          fontSize: '0.66rem',
          fontWeight: 800,
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

MetricChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  tone: PropTypes.string,
};

MetricChip.defaultProps = {
  tone: 'neutral',
};

/* ─────────────────────────────────────────────────────────────────────────
 * Evidence table — clean StockStrength-style layout
 *   · uppercase gray column headers, alternating zebra rows, slim borders
 *   · color reserved only for VALUE text — no tinted backgrounds, no stripes
 * ───────────────────────────────────────────────────────────────────────── */

const evidenceThSx = {
  fontWeight: 700,
  fontSize: '0.62rem',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  py: 0.75,
  px: 1.25,
  whiteSpace: 'nowrap',
  lineHeight: 1.3,
};

const evidenceTdSx = {
  py: 0.7,
  px: 1.25,
  fontSize: '0.72rem',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'middle',
};

function MetricInline({ label, value, tone, lang }) {
  const color = toneColor[tone] || '#475569';
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.35 }}>
      <Typography
        component="span"
        sx={{
          fontSize: '0.6rem',
          fontWeight: 600,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {pickLang(label, lang)}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {pickLang(value, lang)}
      </Typography>
    </Box>
  );
}

MetricInline.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ en: PropTypes.string, vi: PropTypes.string }),
  ]).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({ en: PropTypes.string, vi: PropTypes.string }),
  ]).isRequired,
  tone: PropTypes.string,
  lang: PropTypes.string,
};

MetricInline.defaultProps = {
  tone: 'neutral',
  lang: 'en',
};

function EvidenceTickerRow({ row, zebra, lang }) {
  const headlineColor = toneColor[row.headline_metric_tone] || '#0f172a';
  const insightColor = toneColor[row.insight_tone] || '#64748b';
  const sectorText = sectorDisplay(row, lang);
  return (
    <TableRow
      sx={{
        bgcolor: zebra ? '#fafbfc' : 'transparent',
        '&:hover': { bgcolor: alpha('#3b82f6', 0.04) },
        transition: 'background-color 0.15s ease',
      }}
    >
      <TableCell sx={evidenceTdSx}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
          {row.ticker}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...evidenceTdSx, color: '#94a3b8' }}>
        <Typography
          noWrap
          title={row.sector || ''}
          sx={{
            fontSize: '0.66rem',
            color: '#64748b',
            fontWeight: 600,
            letterSpacing: 0.1,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {sectorText}
        </Typography>
      </TableCell>
      <TableCell sx={evidenceTdSx} align="right">
        <Typography
          sx={{
            fontSize: '0.76rem',
            fontWeight: 700,
            color: headlineColor,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {row.headline_metric}
        </Typography>
      </TableCell>
      <TableCell sx={evidenceTdSx}>
        {Array.isArray(row.metrics) && row.metrics.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25, alignItems: 'baseline' }}>
            {row.metrics.map((m, idx) => (
              <React.Fragment key={`${row.ticker}-m-${idx}`}>
                {idx > 0 ? (
                  <Typography component="span" sx={{ fontSize: '0.6rem', color: '#cbd5e1' }}>·</Typography>
                ) : null}
                <MetricInline label={m.label} value={m.value} tone={m.tone} lang={lang} />
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>
        )}
      </TableCell>
      <TableCell sx={evidenceTdSx}>
        <Typography
          title={pickLang(row.insight, lang) || ''}
          sx={{
            fontSize: '0.7rem',
            color: insightColor,
            fontWeight: 500,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {pickLang(row.insight, lang) || '—'}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

EvidenceTickerRow.propTypes = {
  row: PropTypes.shape({
    ticker: PropTypes.string,
    sector: PropTypes.string,
    sector_name_en: PropTypes.string,
    sector_name_vi: PropTypes.string,
    headline_metric: PropTypes.string,
    headline_metric_tone: PropTypes.string,
    metrics: PropTypes.array,
    insight: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ en: PropTypes.string, vi: PropTypes.string }),
    ]),
    insight_tone: PropTypes.string,
  }).isRequired,
  zebra: PropTypes.bool.isRequired,
  lang: PropTypes.string,
};

EvidenceTickerRow.defaultProps = {
  lang: 'en',
};

function EvidenceSectionHeaderRow({ label, count, accent, summary, summaryTone, emptyText, lang }) {
  const summaryColor = toneColor[summaryTone] || '#64748b';
  const isEmpty = count === 0;
  const summaryText = pickLang(summary, lang);
  return (
    <TableRow sx={{ bgcolor: '#f8fafc' }}>
      <TableCell
        colSpan={5}
        sx={{
          py: 0.6,
          px: 1.25,
          borderTop: '1px solid #e2e8f0',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            component="span"
            sx={{
              fontSize: '0.64rem',
              fontWeight: 800,
              color: accent,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {label}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '0.62rem',
              fontWeight: 700,
              color: '#94a3b8',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {count}
          </Typography>
          {(summaryText || isEmpty) ? (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#cbd5e1' }} />
              <Typography
                component="span"
                sx={{
                  fontSize: '0.68rem',
                  color: isEmpty && !summaryText ? '#cbd5e1' : summaryColor,
                  fontWeight: 500,
                  fontStyle: isEmpty && !summaryText ? 'italic' : 'normal',
                  lineHeight: 1.3,
                }}
              >
                {summaryText || emptyText}
              </Typography>
            </>
          ) : null}
        </Box>
      </TableCell>
    </TableRow>
  );
}

EvidenceSectionHeaderRow.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  accent: PropTypes.string.isRequired,
  summary: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ en: PropTypes.string, vi: PropTypes.string }),
  ]),
  summaryTone: PropTypes.string,
  emptyText: PropTypes.string.isRequired,
  lang: PropTypes.string,
};

EvidenceSectionHeaderRow.defaultProps = {
  summary: '',
  summaryTone: 'neutral',
  lang: 'en',
};

export function RadarEvidenceBoard({ summary, t, lang }) {
  const ev = summary?.evidence || {};
  const empty = t('marketInternalRadar.evidence.empty', 'No symbols');

  const sections = [
    { key: 'top_leaders', tone: 'success', label: t('marketInternalRadar.evidence.topLeaders', 'Top leaders') },
    { key: 'fresh_breakouts', tone: 'info', label: t('marketInternalRadar.evidence.freshBreakouts', 'Fresh breakouts') },
    { key: 'fragile_names', tone: 'warning', label: t('marketInternalRadar.evidence.fragileNames', 'Fragile setups') },
    { key: 'failed_breakouts', tone: 'danger', label: t('marketInternalRadar.evidence.failedBreakouts', 'Failed breakouts') },
    { key: 'top_distribution', tone: 'warning', label: t('marketInternalRadar.evidence.topDistribution', 'Distribution pressure') },
  ];

  return (
    <Box sx={surfaceSx}>
      <Box sx={{ px: 2, pt: 1.5, pb: 0.75 }}>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
          {t('marketInternalRadar.evidence.title', 'Verification evidence')}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
          {t('marketInternalRadar.evidence.subtitle', 'Concrete symbols behind every headline metric')}
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...evidenceThSx, width: 64 }}>
                {t('marketInternalRadar.evidence.colTicker', 'Ticker')}
              </TableCell>
              <TableCell sx={{ ...evidenceThSx, width: 180 }}>
                {t('marketInternalRadar.evidence.colSector', 'Sector')}
              </TableCell>
              <TableCell sx={{ ...evidenceThSx, width: 96 }} align="right">
                {t('marketInternalRadar.evidence.colMetric', 'Metric')}
              </TableCell>
              <TableCell sx={{ ...evidenceThSx, width: 280 }}>
                {t('marketInternalRadar.evidence.colDetails', 'Details')}
              </TableCell>
              <TableCell sx={evidenceThSx}>
                {t('marketInternalRadar.evidence.colInsight', 'Insight')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sections.map((section) => {
              const column = ev[section.key] || {};
              const rows = Array.isArray(column.rows) ? column.rows : [];
              const accent = toneColor[section.tone] || '#64748b';
              const sectionSummary = column.summary || '';
              const summaryTone = column.summary_tone || 'neutral';

              return (
                <React.Fragment key={section.key}>
                  <EvidenceSectionHeaderRow
                    label={section.label}
                    count={rows.length}
                    accent={accent}
                    summary={sectionSummary}
                    summaryTone={summaryTone}
                    emptyText={empty}
                    lang={lang}
                  />
                  {rows.map((row, rowIdx) => (
                    <EvidenceTickerRow
                      key={`${section.key}-${row.ticker}`}
                      row={row}
                      zebra={rowIdx % 2 === 1}
                      lang={lang}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

RadarEvidenceBoard.propTypes = {
  summary: PropTypes.shape({
    evidence: PropTypes.shape({
      top_leaders: PropTypes.object,
      fresh_breakouts: PropTypes.object,
      fragile_names: PropTypes.object,
      failed_breakouts: PropTypes.object,
      top_distribution: PropTypes.object,
    }),
  }),
  t: PropTypes.func.isRequired,
  lang: PropTypes.string,
};

RadarEvidenceBoard.defaultProps = {
  summary: null,
  lang: 'en',
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarMarketPulse — single hero card with the headline quality + pressures  */
/* ────────────────────────────────────────────────────────────────────────── */

function PressureRow({ label, value, max, color }) {
  const safeMax = Math.max(Number(max) || 0, Number(value) || 0, 1);
  const ratio = Math.min(100, ((Number(value) || 0) / safeMax) * 100);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 36px 96px', alignItems: 'center', gap: 1 }}>
      <Typography noWrap sx={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color, textAlign: 'right' }}>
        {value}
      </Typography>
      <Box sx={{ position: 'relative', height: 6, borderRadius: 999, bgcolor: alpha(color, 0.12), overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${ratio}%`,
            bgcolor: color,
            borderRadius: 999,
            transition: 'width 0.25s ease',
          }}
        />
      </Box>
    </Box>
  );
}

PressureRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string.isRequired,
};

PressureRow.defaultProps = {
  max: 0,
};

export function RadarMarketPulse({ summary, candidateSummary, t }) {
  const s = summary || {};
  const cs = candidateSummary || {};
  const marketLabel = s.market_quality_label || 'UNKNOWN';
  const qualityScore = Number(s.market_quality_score || 0);
  const qColor = marketQualityColor(marketLabel);
  const qBg = marketQualityBg(marketLabel);
  const headlineMeta = normalizeHeadline(cs);

  const breakouts = (s.breakout_20_count || 0) + (s.breakout_60_count || 0);
  const breakdowns = (s.breakdown_20_count || 0) + (s.breakdown_60_count || 0);
  const validated = cs.validated_count || 0;
  const fragile = cs.fragile_count || 0;
  const pressureMax = Math.max(breakouts, breakdowns, validated, fragile, 10);

  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={RadarIcon}
        title={t('marketInternalRadar.pulse.title', 'Market Pulse')}
        subtitle={t('marketInternalRadar.pulse.subtitle', 'Headline read with confirming pressure')}
        action={
          <Chip
            size="small"
            label={t(headlineMeta.labelKey, headlineMeta.fallback)}
            sx={{
              bgcolor: toneBg[headlineMeta.tone],
              color: toneColor[headlineMeta.tone],
              fontWeight: 700,
              height: 22,
              fontSize: '0.7rem',
            }}
          />
        }
      />
      <Box sx={{ p: 1.25, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Hero score block — single compact row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${qBg} 0%, #ffffff 70%)`,
            border: `1px solid ${alpha(qColor, 0.22)}`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {t('marketInternalRadar.stats.marketQuality', 'Market Quality')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.15 }}>
              <Typography sx={{ fontSize: '1.85rem', fontWeight: 900, color: qColor, lineHeight: 1 }}>
                {formatNumber(qualityScore, 2)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700 }}>
                / 100
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: 999,
              bgcolor: qColor,
              color: '#fff',
              fontSize: '0.66rem',
              fontWeight: 800,
              letterSpacing: 0.4,
              boxShadow: `0 3px 8px ${alpha(qColor, 0.3)}`,
              flexShrink: 0,
            }}
          >
            {marketLabel}
          </Box>
        </Box>

        {/* Pressure rows */}
        <Stack spacing={0.75} sx={{ mt: 0.25 }}>
          <PressureRow
            label={t('marketInternalRadar.stats.breakoutPressure', 'Breakout 20D + 60D')}
            value={breakouts}
            max={pressureMax}
            color="#10b981"
          />
          <PressureRow
            label={t('marketInternalRadar.stats.breakdownPressure', 'Breakdown 20D + 60D')}
            value={breakdowns}
            max={pressureMax}
            color="#ef4444"
          />
          <PressureRow
            label={t('marketInternalRadar.candidates.validated', 'Validated')}
            value={validated}
            max={pressureMax}
            color="#2563eb"
          />
          <PressureRow
            label={t('marketInternalRadar.candidates.fragile', 'Fragile')}
            value={fragile}
            max={pressureMax}
            color="#d97706"
          />
        </Stack>
      </Box>
    </Box>
  );
}

RadarMarketPulse.propTypes = {
  summary: PropTypes.shape({
    market_quality_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    market_quality_label: PropTypes.string,
    breakout_20_count: PropTypes.number,
    breakout_60_count: PropTypes.number,
    breakdown_20_count: PropTypes.number,
    breakdown_60_count: PropTypes.number,
    semantics: PropTypes.shape({
      market_bias: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string }),
      index_read: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string }),
    }),
  }),
  candidateSummary: PropTypes.shape({
    validated_count: PropTypes.number,
    fragile_count: PropTypes.number,
    headline: PropTypes.shape({ key: PropTypes.string, label_key: PropTypes.string, tone: PropTypes.string }),
  }),
  t: PropTypes.func.isRequired,
};

RadarMarketPulse.defaultProps = {
  summary: null,
  candidateSummary: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarBreadthSnapshot — secondary metrics as compact list                   */
/* ────────────────────────────────────────────────────────────────────────── */

function SnapshotRow({ label, valueText, ratio, color, divider }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.45 }}>
        <Typography noWrap sx={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: color || '#0f172a' }}>
          {valueText}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', height: 5, borderRadius: 999, bgcolor: alpha(color || '#94a3b8', 0.12), overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${Math.max(0, Math.min(100, ratio))}%`,
            bgcolor: color || '#94a3b8',
            borderRadius: 999,
            transition: 'width 0.25s ease',
          }}
        />
      </Box>
      {divider ? <Divider sx={{ borderColor: '#f1f5f9', mt: 1 }} /> : null}
    </Box>
  );
}

SnapshotRow.propTypes = {
  label: PropTypes.string.isRequired,
  valueText: PropTypes.string.isRequired,
  ratio: PropTypes.number.isRequired,
  color: PropTypes.string,
  divider: PropTypes.bool,
};

SnapshotRow.defaultProps = {
  color: '',
  divider: false,
};

export function RadarBreadthSnapshot({ summary, t }) {
  const s = summary || {};
  const ma20 = (s.pct_above_ma20 || 0) * 100;
  const ma50 = (s.pct_above_ma50 || 0) * 100;
  const leaders = s.leaders_count || 0;
  const watchlist = s.watchlist_count || 0;
  const distribution = s.distribution_warning_count || 0;
  const failed = s.failed_breakout_count || 0;
  const leadershipMax = Math.max(leaders, watchlist, 10);
  const riskMax = Math.max(distribution, failed, 10);

  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={InsightsIcon}
        title={t('marketInternalRadar.breadth.title', 'Breadth Snapshot')}
        subtitle={t('marketInternalRadar.breadth.subtitle', 'Quality, leadership, and risk gauges')}
      />
      <Stack spacing={0.85} sx={{ p: 1.25, flex: 1 }}>
        <SnapshotRow
          label={t('marketInternalRadar.summary.aboveMA20', 'Pct above MA20')}
          valueText={formatPercent(ma20, 1)}
          ratio={ma20}
          color="#2563eb"
        />
        <SnapshotRow
          label={t('marketInternalRadar.summary.aboveMA50', 'Pct above MA50')}
          valueText={formatPercent(ma50, 1)}
          ratio={ma50}
          color="#6366f1"
        />
        <SnapshotRow
          label={t('marketInternalRadar.summary.leaders', 'Leaders')}
          valueText={String(leaders)}
          ratio={(leaders / leadershipMax) * 100}
          color="#10b981"
        />
        <SnapshotRow
          label={t('marketInternalRadar.summary.watchlist', 'Watchlist')}
          valueText={String(watchlist)}
          ratio={(watchlist / leadershipMax) * 100}
          color="#0ea5e9"
        />
        <SnapshotRow
          label={t('marketInternalRadar.summary.distributionWarnings', 'Distribution warnings')}
          valueText={String(distribution)}
          ratio={(distribution / riskMax) * 100}
          color="#d97706"
        />
        <SnapshotRow
          label={t('marketInternalRadar.summary.failedBreakouts', 'Failed breakouts')}
          valueText={String(failed)}
          ratio={(failed / riskMax) * 100}
          color="#ef4444"
        />
      </Stack>
    </Box>
  );
}

RadarBreadthSnapshot.propTypes = {
  summary: PropTypes.shape({
    pct_above_ma20: PropTypes.number,
    pct_above_ma50: PropTypes.number,
    leaders_count: PropTypes.number,
    watchlist_count: PropTypes.number,
    distribution_warning_count: PropTypes.number,
    failed_breakout_count: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};

RadarBreadthSnapshot.defaultProps = {
  summary: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarKpiStrip — kept as a thin wrapper for backwards compatibility          */
/* ────────────────────────────────────────────────────────────────────────── */

export function RadarKpiStrip({ summary, candidateSummary, t }) {
  // Composed view: legacy entry-point renders the new Pulse + Breadth pair.
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 1.5,
      }}
    >
      <RadarMarketPulse summary={summary} candidateSummary={candidateSummary} t={t} />
      <RadarBreadthSnapshot summary={summary} t={t} />
    </Box>
  );
}

RadarKpiStrip.propTypes = {
  summary: PropTypes.shape({}),
  candidateSummary: PropTypes.shape({}),
  t: PropTypes.func.isRequired,
};

RadarKpiStrip.defaultProps = {
  summary: null,
  candidateSummary: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarInsightsPanel — semantics insights cards                              */
/* ────────────────────────────────────────────────────────────────────────── */

export function RadarInsightsPanel({ semantics, t }) {
  if (!semantics) return null;
  const insights = semantics.insights || [];
  const headlineSeverity = toneToAlertSeverity(semantics.headline?.tone);

  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={InsightsIcon}
        title={t('marketInternalRadar.semantics.title', 'Market Interpretation')}
        subtitle={t('marketInternalRadar.semantics.subtitle', 'Backend-owned reading of the tape')}
      />
      <Stack spacing={0.85} sx={{ p: 1.25, flex: 1 }}>
        <Alert
          severity={headlineSeverity}
          sx={{ borderRadius: 1.5, py: 0.1, '& .MuiAlert-message': { fontSize: '0.74rem', fontWeight: 700 } }}
        >
          {translateSemantic(t, semantics.headline, t('marketInternalRadar.semantics.defaultHeadline', 'Market internals are mixed.'))}
        </Alert>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            label={`${t('marketInternalRadar.bias', 'Bias')}: ${translateSemantic(t, semantics.market_bias, semantics.market_bias?.key)}`}
            sx={{
              bgcolor: toneBg[semantics.market_bias?.tone || 'warning'],
              color: toneColor[semantics.market_bias?.tone || 'warning'],
              fontWeight: 700,
              height: 22,
              fontSize: '0.68rem',
            }}
          />
          <Chip
            size="small"
            label={`${t('marketInternalRadar.indexRead', 'Index Read')}: ${translateSemantic(t, semantics.index_read, semantics.index_read?.key)}`}
            sx={{
              bgcolor: toneBg[semantics.index_read?.tone || 'info'],
              color: toneColor[semantics.index_read?.tone || 'info'],
              fontWeight: 700,
              height: 22,
              fontSize: '0.68rem',
            }}
          />
        </Stack>
        <Stack spacing={0.6} sx={{ mt: 0.25 }}>
          {insights.length ? insights.map((item) => {
            const tone = item.tone || 'info';
            return (
              <Box
                key={item.key}
                sx={{
                  borderRadius: 1.5,
                  border: '1px solid #edf2f7',
                  background: `linear-gradient(180deg, ${alpha(toneColor[tone], 0.06)} 0%, #ffffff 100%)`,
                  px: 1,
                  py: 0.6,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <TipsAndUpdatesIcon sx={{ fontSize: '0.95rem', color: toneColor[tone], flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', flex: 1, minWidth: 0 }}>
                    {translateSemantic(t, item, item.key)}
                  </Typography>
                  {'value' in item ? (
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: toneColor[tone], flexShrink: 0 }}>
                      {item.value}
                    </Typography>
                  ) : null}
                </Stack>
                {('leaders' in item || 'watchlist' in item) ? (
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mt: 0.35, pl: 2.4 }}>
                    {`${t('marketInternalRadar.summary.leaders', 'Leaders')}: ${item.leaders || 0} · ${t('marketInternalRadar.summary.watchlist', 'Watchlist')}: ${item.watchlist || 0}`}
                  </Typography>
                ) : null}
              </Box>
            );
          }) : (
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
              {t('marketInternalRadar.semantics.noInsights', 'No additional breadth insights for this snapshot.')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

RadarInsightsPanel.propTypes = {
  semantics: PropTypes.shape({
    headline: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string, label_key: PropTypes.string }),
    market_bias: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string, label_key: PropTypes.string }),
    index_read: PropTypes.shape({ key: PropTypes.string, tone: PropTypes.string, label_key: PropTypes.string }),
    insights: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      tone: PropTypes.string,
      label_key: PropTypes.string,
      value: PropTypes.number,
      leaders: PropTypes.number,
      watchlist: PropTypes.number,
    })),
  }),
  t: PropTypes.func.isRequired,
};

RadarInsightsPanel.defaultProps = {
  semantics: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarRiskPanel — risk warnings                                             */
/* ────────────────────────────────────────────────────────────────────────── */

export function RadarRiskPanel({ semantics, t }) {
  const warnings = semantics?.risk_warnings || [];
  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={ShieldOutlinedIcon}
        title={t('marketInternalRadar.semantics.riskTitle', 'Risk Warnings')}
        subtitle={t('marketInternalRadar.semantics.riskSubtitle', 'Fragility and distribution clusters')}
      />
      <Stack spacing={0.6} sx={{ p: 1.25, flex: 1 }}>
        {warnings.length ? warnings.map((item) => (
          <Alert
            key={item.key}
            severity={item.tone === 'danger' ? 'error' : 'warning'}
            icon={<WarningAmberIcon fontSize="inherit" />}
            sx={{ borderRadius: 1.5, py: 0.25, '& .MuiAlert-message': { width: '100%', py: 0.25 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                {translateSemantic(t, item, item.key)}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>
                {item.value || 0}
              </Typography>
            </Box>
          </Alert>
        )) : (
          <Alert severity="success" sx={{ borderRadius: 1.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
            {t('marketInternalRadar.semantics.noWarnings', 'No major failure cluster or distribution expansion is flagged in this snapshot.')}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}

RadarRiskPanel.propTypes = {
  semantics: PropTypes.shape({
    risk_warnings: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      tone: PropTypes.string,
      label_key: PropTypes.string,
      value: PropTypes.number,
    })),
  }),
  t: PropTypes.func.isRequired,
};

RadarRiskPanel.defaultProps = {
  semantics: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarCandidatesSection — main candidate table                              */
/* ────────────────────────────────────────────────────────────────────────── */

export function RadarCandidatesSection({ candidatesPayload, loading, t }) {
  const candidates = candidatesPayload?.candidates || [];
  const summary = candidatesPayload?.summary;
  let bodyContent;

  if (loading && !candidates.length) {
    bodyContent = (
      <TableRow>
        <TableCell colSpan={8} sx={{ ...tableCellSx, textAlign: 'center', color: '#94a3b8' }}>
          {t('marketInternalRadar.loading', 'Loading radar data...')}
        </TableCell>
      </TableRow>
    );
  } else if (candidates.length) {
    bodyContent = candidates.map((candidate) => {
      const setupMeta = getSetupMeta(candidate.candidate_setup);
      const validationMeta = getValidationMeta(candidate.semantics?.validation?.key);
      const setupLabel = translateSemantic(t, candidate.semantics?.setup, t(setupMeta.labelKey, setupMeta.fallback));
      const validationLabel = translateSemantic(t, candidate.semantics?.validation, t(validationMeta.labelKey, validationMeta.fallback));
      return (
        <TableRow key={`${candidate.ticker}-${candidate.trade_date}`} hover>
          <TableCell sx={tableCellSx}>
            <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.78rem' }}>{candidate.ticker}</Typography>
            <Typography noWrap sx={{ fontSize: '0.7rem', color: '#64748b', maxWidth: 220 }}>
              {candidate.company_name || '—'}
            </Typography>
          </TableCell>
          <TableCell sx={tableCellSx}>
            <Chip
              size="small"
              label={setupLabel}
              sx={{ bgcolor: toneBg[setupMeta.tone], color: toneColor[setupMeta.tone], fontWeight: 700, height: 22, fontSize: '0.7rem' }}
            />
          </TableCell>
          <TableCell sx={tableCellSx}>
            <Chip
              size="small"
              label={validationLabel}
              sx={{ bgcolor: toneBg[validationMeta.tone], color: toneColor[validationMeta.tone], fontWeight: 700, height: 22, fontSize: '0.7rem' }}
            />
          </TableCell>
          <TableCell sx={{ ...tableCellSx, fontWeight: 800, color: '#0f172a' }}>
            {formatNumber(candidate.radar_score, 1)}
          </TableCell>
          <TableCell sx={tableCellSx}>{candidate.rank_label}</TableCell>
          <TableCell sx={tableCellSx}>
            <Chip
              size="small"
              label={`${candidate.market_quality_label} · ${formatNumber(candidate.market_quality_score, 2)}`}
              sx={{
                bgcolor: marketQualityBg(candidate.market_quality_label),
                color: marketQualityColor(candidate.market_quality_label),
                fontWeight: 700,
                height: 22,
                fontSize: '0.7rem',
              }}
            />
          </TableCell>
          <TableCell sx={tableCellSx}>{formatRatio(candidate.rs_vnindex_20, 4)}</TableCell>
          <TableCell sx={tableCellSx}>{formatRatio(candidate.vol_ratio, 2)}</TableCell>
        </TableRow>
      );
    });
  } else {
    bodyContent = (
      <TableRow>
        <TableCell colSpan={8} sx={{ ...tableCellSx, textAlign: 'center', color: '#94a3b8' }}>
          {t('marketInternalRadar.candidates.empty', 'No candidates matched the current filters.')}
        </TableCell>
      </TableRow>
    );
  }

  const headlineChip = summary ? (
    <Chip
      size="small"
      label={`${t(normalizeHeadline(summary).labelKey, normalizeHeadline(summary).fallback)} · ${t('marketInternalRadar.candidates.total', 'Total')}: ${candidatesPayload.total || 0}`}
      sx={{
        bgcolor: toneBg[normalizeHeadline(summary).tone],
        color: toneColor[normalizeHeadline(summary).tone],
        fontWeight: 700,
        height: 22,
        fontSize: '0.7rem',
      }}
    />
  ) : null;

  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={CandlestickChartIcon}
        title={t('marketInternalRadar.candidates.title', 'Candidate Board')}
        subtitle={t('marketInternalRadar.candidates.subtitle', 'Validated names first, fragile names second')}
        action={headlineChip}
      />
      {summary ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ px: 1.25, py: 0.85, borderBottom: '1px solid #f1f5f9', bgcolor: '#fbfcfe' }}>
          <Chip size="small" label={`SPRING: ${summary.setup_distribution?.SPRING || 0}`} sx={{ bgcolor: '#ecfdf5', color: '#10b981', fontWeight: 700, height: 22, fontSize: '0.7rem' }} />
          <Chip size="small" label={`BREAKOUT: ${summary.setup_distribution?.BREAKOUT || 0}`} sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 700, height: 22, fontSize: '0.7rem' }} />
          <Chip size="small" label={`${t('marketInternalRadar.candidates.validated', 'Validated')}: ${summary.validated_count || 0}`} sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700, height: 22, fontSize: '0.7rem' }} />
          <Chip size="small" label={`${t('marketInternalRadar.candidates.fragile', 'Fragile')}: ${summary.fragile_count || 0}`} sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 700, height: 22, fontSize: '0.7rem' }} />
        </Stack>
      ) : null}
      <TableContainer sx={{ flex: 1, maxHeight: { xs: 'none', lg: 620 } }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.ticker', 'Ticker')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.setup', 'Setup')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.validation', 'Validation')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.score', 'Score')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.rank', 'Rank')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.marketQuality', 'Market Quality')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.rs', 'RS vs VNINDEX')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.candidates.volume', 'Vol Ratio')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{bodyContent}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

RadarCandidatesSection.propTypes = {
  candidatesPayload: PropTypes.shape({
    total: PropTypes.number,
    summary: PropTypes.shape({
      headline: PropTypes.shape({ key: PropTypes.string }),
      validated_count: PropTypes.number,
      fragile_count: PropTypes.number,
      setup_distribution: PropTypes.shape({
        SPRING: PropTypes.number,
        BREAKOUT: PropTypes.number,
        UNVALIDATED: PropTypes.number,
      }),
    }),
    candidates: PropTypes.arrayOf(PropTypes.shape({
      ticker: PropTypes.string,
      trade_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      company_name: PropTypes.string,
      candidate_setup: PropTypes.string,
      candidate_validated: PropTypes.bool,
      rank_label: PropTypes.string,
      radar_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
      market_quality_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
      market_quality_label: PropTypes.string,
      rs_vnindex_20: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
      vol_ratio: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
      semantics: PropTypes.shape({
        setup: PropTypes.shape({ key: PropTypes.string, label_key: PropTypes.string }),
        validation: PropTypes.shape({ key: PropTypes.string, label_key: PropTypes.string }),
      }),
    })),
  }),
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

RadarCandidatesSection.defaultProps = {
  candidatesPayload: null,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarSectorsPanel — sector rotation table                                  */
/* ────────────────────────────────────────────────────────────────────────── */

export function RadarSectorsPanel({ sectors, loading, t, lang }) {
  let body;
  if (loading && !sectors.length) {
    body = (
      <TableRow>
        <TableCell colSpan={5} sx={{ ...tableCellSx, textAlign: 'center', color: '#94a3b8' }}>
          {t('marketInternalRadar.loading', 'Loading radar data...')}
        </TableCell>
      </TableRow>
    );
  } else if (sectors.length) {
    body = sectors.map((sector) => (
      <TableRow key={`${sector.sector}-${sector.trade_date}`} hover>
        <TableCell sx={{ ...tableCellSx, fontWeight: 700, color: '#0f172a' }}>{sectorDisplay(sector, lang)}</TableCell>
        <TableCell sx={{ ...tableCellSx, fontWeight: 700 }}>{formatNumber(sector.sector_flow_score, 2)}</TableCell>
        <TableCell sx={{ ...tableCellSx, color: '#10b981', fontWeight: 700 }}>{`${sector.breakout20 || 0}/${sector.breakout60 || 0}`}</TableCell>
        <TableCell sx={{ ...tableCellSx, color: '#ef4444', fontWeight: 700 }}>{`${sector.breakdown20 || 0}/${sector.breakdown60 || 0}`}</TableCell>
        <TableCell sx={tableCellSx}>{pickLang(sector.sector_label_i18n, lang) || sector.sector_label || '—'}</TableCell>
      </TableRow>
    ));
  } else {
    body = (
      <TableRow>
        <TableCell colSpan={5} sx={{ ...tableCellSx, textAlign: 'center', color: '#94a3b8' }}>
          {t('marketInternalRadar.sectors.empty', 'No sector breadth rows available.')}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={ViewWeekIcon}
        title={t('marketInternalRadar.sectors.title', 'Sector Rotation')}
        subtitle={t('marketInternalRadar.sectors.subtitle', 'Flow confirmation by sector breadth')}
      />
      <TableContainer sx={{ flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.sectors.sector', 'Sector')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.sectors.flow', 'Flow Score')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.sectors.breakout', 'Breakout 20/60')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.sectors.breakdown', 'Breakdown 20/60')}</TableCell>
              <TableCell sx={tableHeadSx}>{t('marketInternalRadar.sectors.label', 'Label')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

RadarSectorsPanel.propTypes = {
  sectors: PropTypes.arrayOf(PropTypes.shape({
    trade_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    sector: PropTypes.string,
    sector_name_en: PropTypes.string,
    sector_name_vi: PropTypes.string,
    sector_flow_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
    breakout20: PropTypes.number,
    breakout60: PropTypes.number,
    breakdown20: PropTypes.number,
    breakdown60: PropTypes.number,
    sector_label: PropTypes.string,
  })),
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  lang: PropTypes.string,
};

RadarSectorsPanel.defaultProps = {
  sectors: [],
  lang: 'en',
};

/* ────────────────────────────────────────────────────────────────────────── */
/* RadarHistoryPanel — breadth chart + compact table                          */
/* ────────────────────────────────────────────────────────────────────────── */

function RadarHistoryTooltip({ active, payload, label, t }) {
  if (!active || !payload?.length) return null;
  const qualityPoint = payload.find((item) => item.dataKey === 'quality');
  const breakoutPoint = payload.find((item) => item.dataKey === 'breakouts');
  const breakdownPoint = payload.find((item) => item.dataKey === 'breakdowns');
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', mb: 0.4 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#2563eb' }}>{`${t('marketInternalRadar.history.quality', 'Quality')}: ${formatNumber(qualityPoint?.value, 2)}`}</Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#10b981' }}>{`${t('marketInternalRadar.history.breakouts', 'Breakouts')}: ${breakoutPoint?.value || 0}`}</Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#ef4444' }}>{`${t('marketInternalRadar.history.breakdowns', 'Breakdowns')}: ${breakdownPoint?.value || 0}`}</Typography>
    </Box>
  );
}

RadarHistoryTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    dataKey: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  label: PropTypes.string,
  t: PropTypes.func.isRequired,
};

RadarHistoryTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

export function RadarHistoryPanel({ history, loading, t, locale }) {
  const chartData = [...history].reverse().map((row) => ({
    date: prettyDate(row.trade_date, locale),
    quality: Number(row.market_quality_score || 0),
    breakouts: Number(row.breakout_20_count || 0) + Number(row.breakout_60_count || 0),
    breakdowns: Number(row.breakdown_20_count || 0) + Number(row.breakdown_60_count || 0),
  }));

  let content;
  if (loading && !chartData.length) {
    content = (
      <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 4 }}>
        {t('marketInternalRadar.loading', 'Loading radar data...')}
      </Typography>
    );
  } else if (chartData.length) {
    content = (
      <>
        <Box sx={{ height: { xs: 240, lg: 280 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="market-internal-quality" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} width={32} domain={[0, 100]} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={28} />
              <RechartsTooltip content={<RadarHistoryTooltip t={t} />} />
              <Area yAxisId="left" type="monotone" dataKey="quality" fill="url(#market-internal-quality)" stroke="none" />
              <Line yAxisId="left" type="monotone" dataKey="quality" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              <Bar yAxisId="right" dataKey="breakouts" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={16} />
              <Bar yAxisId="right" dataKey="breakdowns" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={16} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadSx}>{t('marketInternalRadar.history.date', 'Date')}</TableCell>
                <TableCell sx={tableHeadSx}>{t('marketInternalRadar.history.quality', 'Quality')}</TableCell>
                <TableCell sx={tableHeadSx}>{t('marketInternalRadar.history.breakouts', 'Breakouts')}</TableCell>
                <TableCell sx={tableHeadSx}>{t('marketInternalRadar.history.breakdowns', 'Breakdowns')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.slice(0, 6).map((row) => (
                <TableRow key={String(row.trade_date)} hover>
                  <TableCell sx={tableCellSx}>{prettyDate(row.trade_date, locale)}</TableCell>
                  <TableCell sx={tableCellSx}>{`${formatNumber(row.market_quality_score, 2)} · ${row.market_quality_label}`}</TableCell>
                  <TableCell sx={tableCellSx}>{(row.breakout_20_count || 0) + (row.breakout_60_count || 0)}</TableCell>
                  <TableCell sx={tableCellSx}>{(row.breakdown_20_count || 0) + (row.breakdown_60_count || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  } else {
    content = (
      <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 4 }}>
        {t('marketInternalRadar.history.empty', 'No history rows available.')}
      </Typography>
    );
  }

  return (
    <Box sx={{ ...surfaceSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={InsightsIcon}
        title={t('marketInternalRadar.history.title', 'Breadth History')}
        subtitle={t('marketInternalRadar.history.subtitle', 'Quality vs breakout and breakdown expansion')}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: 2, bgcolor: '#2563eb' }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700 }}>
                {t('marketInternalRadar.history.quality', 'Quality')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: '#10b981' }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700 }}>
                {t('marketInternalRadar.history.breakouts', 'Breakouts')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: '#ef4444' }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700 }}>
                {t('marketInternalRadar.history.breakdowns', 'Breakdowns')}
              </Typography>
            </Stack>
          </Stack>
        }
      />
      <Box sx={{ p: 1.25, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {content}
      </Box>
    </Box>
  );
}

RadarHistoryPanel.propTypes = {
  history: PropTypes.arrayOf(PropTypes.shape({
    trade_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    market_quality_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
    market_quality_label: PropTypes.string,
    breakout_20_count: PropTypes.number,
    breakout_60_count: PropTypes.number,
    breakdown_20_count: PropTypes.number,
    breakdown_60_count: PropTypes.number,
  })),
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
};

RadarHistoryPanel.defaultProps = {
  history: [],
};

// Legacy combined renderer kept for any external callers (renders inside a Grid)
export function RadarSectorsHistorySection({ sectors, history, loading, t, locale }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.2fr 1fr' }, gap: 2 }}>
      <RadarSectorsPanel sectors={sectors} loading={loading} t={t} />
      <RadarHistoryPanel history={history} loading={loading} t={t} locale={locale} />
    </Box>
  );
}

RadarSectorsHistorySection.propTypes = {
  sectors: PropTypes.arrayOf(PropTypes.shape({})),
  history: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
};

RadarSectorsHistorySection.defaultProps = {
  sectors: [],
  history: [],
};

// Backwards-compatible aliases for previous component names
export const RadarSemanticsSection = RadarInsightsPanel;

export function RadarSummaryGrid() {
  // Replaced by RadarKpiStrip — kept as a no-op for backwards compatibility.
  return null;
}
